import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Circle } from 'lucide-react';
import { getMarkets, listEntities, fetchDataFreshness, type DataFreshness } from '../api/client.js';
import { useScreenerStats } from '../api/screener-hooks.js';
import { useDockviewApi } from './dockview-context.js';
import { useActiveTabId, useAppStore } from './store.js';
import {
  describeOps,
  LARGE_PROTECTED_OPERATION_ROWS,
  largeUnrestoredOperations,
} from './protected-changes.js';

// Always-on bottom status bar — the SaxoTrader-style "trading floor"
// element that tells the operator what they're connected to and how
// fresh the data is. Three sections:
//   - API health    (green dot when /markets responds)
//   - Entity count  (total entities visible in the database)
//   - Cmd+K hint    (so users discover the command palette)
//
// Each query runs at a slow polling interval; the bar doesn't refetch
// on every render. We poll markets every 60s as a cheap connectivity
// heartbeat.

export function StatusBar({ onOpenFeedback }: { onOpenFeedback?: () => void }) {
  const markets = useQuery({
    queryKey: ['status-bar', 'markets'],
    queryFn: getMarkets,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const entityCount = useQuery({
    queryKey: ['status-bar', 'entity-count'],
    queryFn: () => listEntities({ limit: 1 }),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  // Per-market data recency — markets recompute independently, so we surface
  // the freshest as a headline and the full breakdown on hover (never one
  // conflated timestamp).
  const freshness = useQuery({
    queryKey: ['status-bar', 'freshness'],
    queryFn: fetchDataFreshness,
    refetchInterval: 10 * 60_000,
    staleTime: 5 * 60_000,
  });

  // Quarantine count — universe-wide indicator of rows flagged by the
  // screener row-quality checks. Click spawns a floating Screener
  // panel pre-configured with the quarantined-only filter.
  const screenerStats = useScreenerStats();
  const dockviewApiRef = useDockviewApi();
  const activeTabId = useActiveTabId();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);

  function openQuarantinedScreener() {
    const api = dockviewApiRef.current;
    if (!api || !activeTabId) return;
    // Create the panel-state entry in the store first so the panel id
    // and channel-link metadata exist before dockview tries to mount
    // the component (which calls usePanel(...) via the store).
    const newId = addPanelToStore(activeTabId, {
      component: 'screener',
      title: 'Quarantined Companies',
    });
    // Cascade-offset the new floating panel so consecutive opens don't
    // stack exactly on top of each other.
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel({
      id: newId,
      component: 'screener',
      title: 'Quarantined Companies',
      params: {
        tabId: activeTabId,
        panelId: newId,
        initialState: { quarantineOnly: true },
      },
      floating: {
        position: { left: 120 + offset, top: 80 + offset },
        width: 720,
        height: 520,
      },
    });
  }

  const apiOnline = markets.isSuccess;
  const marketCount = markets.data?.markets.length ?? 0;
  const totalEntities = entityCount.data?.pagination.total ?? 0;
  const quarantinedCount = screenerStats.data?.quarantined ?? 0;
  const failingJobCount = freshness.data?.failingJobs?.length ?? 0;

  // The pill reports the SYSTEM, not just the API socket.
  //
  // It used to read `apiOnline ? 'Healthy' : 'Unhealthy'` — purely "does
  // /markets respond". That was already a stretch, and it became flatly
  // self-contradictory the moment the failing-jobs badge shipped beside it:
  // the bar said "Healthy · ⚠ 14 jobs failing" in the same breath on
  // 2026-07-21. If a label can sit next to its own refutation, it isn't
  // reporting on the thing users think it is.
  //
  // DEGRADED is the missing middle: the app serves fine, but the data behind
  // it is not being refreshed. That is precisely the state that hid an
  // eight-day outage — everything looked alive because everything WAS alive,
  // except the jobs. Green must mean "and the pipeline ran", or green is
  // just an uptime check wearing a broader word.
  const systemStatus = !apiOnline
    ? {
        label: 'Unhealthy',
        colour: 'var(--color-negative-500)',
        detail: 'The API is not responding.',
      }
    : failingJobCount > 0
      ? {
          label: 'Degraded',
          colour: 'var(--color-warning-500)',
          detail:
            `The API is healthy but ${failingJobCount} scheduled ` +
            `job${failingJobCount === 1 ? '' : 's'} last failed — data may be stale. ` +
            `Hover the red badge for the list.`,
        }
      : {
          label: 'Healthy',
          colour: 'var(--color-positive-500)',
          detail: 'API responding and every scheduled job last ran clean.',
        };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        padding: '4px var(--space-3)',
        borderTop: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface)',
        fontFamily: 'var(--font-mono)',
        fontSize: '11px',
        color: 'var(--fg-muted)',
        minHeight: '22px',
      }}
    >
      <StatusItem>
        <Circle size={8} fill={systemStatus.colour} stroke="none" />
        <span title={systemStatus.detail}>{systemStatus.label}</span>
      </StatusItem>
      <Sep />
      <StatusItem>
        <span data-numeric>{marketCount}</span>
        <span>markets</span>
      </StatusItem>
      <Sep />
      <StatusItem>
        <span data-numeric>{totalEntities.toLocaleString('en-GB')}</span>
        <span>entities</span>
      </StatusItem>
      {freshness.data && (
        <>
          <Sep />
          <FreshnessItem data={freshness.data} />
        </>
      )}
      {quarantinedCount > 0 && (
        <>
          <Sep />
          <StatusItem>
            <button
              type="button"
              onClick={openQuarantinedScreener}
              title="Open Triage pinned to quarantined rows only — modelled inputs that failed validation."
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                background: 'transparent',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <AlertTriangle
                size={11}
                style={{ color: 'var(--color-warning-500, #b45309)' }}
                aria-hidden
              />
              <span data-numeric>{quarantinedCount.toLocaleString('en-GB')}</span>
              <span style={{ textDecoration: 'underline dotted' }}>quarantined</span>
            </button>
          </StatusItem>
        </>
      )}
      <div style={{ flex: 1 }} />
      {onOpenFeedback && (
        <>
          <StatusItem>
            <button
              type="button"
              onClick={onOpenFeedback}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                textDecoration: 'underline dotted',
              }}
            >
              Give feedback
            </button>
          </StatusItem>
          <Sep />
        </>
      )}
      <ClockBlock />
    </div>
  );
}

// Live wall-clock readout. Re-renders once per minute — second-precision
// isn't useful here and would needlessly burn renders.
function ClockBlock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // Schedule the first tick to land on the next minute boundary so all
    // subsequent ticks happen exactly when the minute changes.
    let intervalId = 0;
    const msToNextMinute = 60_000 - (Date.now() % 60_000);
    const initialId = window.setTimeout(() => {
      setNow(new Date());
      intervalId = window.setInterval(() => setNow(new Date()), 60_000);
    }, msToNextMinute);
    return () => {
      window.clearTimeout(initialId);
      if (intervalId !== 0) window.clearInterval(intervalId);
    };
  }, []);
  const cities: Array<{ label: string; tz: string }> = [
    { label: 'London', tz: 'Europe/London' },
    { label: 'Paris', tz: 'Europe/Paris' },
    { label: 'Warsaw', tz: 'Europe/Warsaw' },
    { label: 'Athens', tz: 'Europe/Athens' },
  ];
  return (
    <StatusItem>
      {cities.map((c, i) => (
        <span key={c.tz} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {i > 0 && <span style={{ opacity: 0.4, margin: '0 2px' }}>·</span>}
          <span style={{ color: 'var(--text-tertiary)' }}>{c.label}</span>
          <span data-numeric style={{ whiteSpace: 'nowrap' }}>
            {now.toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: c.tz,
            })}
          </span>
        </span>
      ))}
    </StatusItem>
  );
}

function StatusItem({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
      {children}
    </span>
  );
}

function Sep() {
  return <span style={{ opacity: 0.4 }}>·</span>;
}

// Postgres timestamptz::text comes through as "2026-06-20 06:15:00.123+00";
// normalise to something every JS engine parses (T separator, padded offset).
function parseTs(iso: string | null): number {
  if (!iso) return NaN;
  let s = iso.trim().replace(' ', 'T');
  if (/[+-]\d\d$/.test(s)) s += ':00';
  return new Date(s).getTime();
}
function relativeTime(iso: string | null): string {
  const t = parseTs(iso);
  if (!Number.isFinite(t)) return '—';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}
function shortStamp(iso: string | null): string {
  const t = parseTs(iso);
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Per-market data recency. Headline = the freshest market's metrics recompute;
// the hover tooltip breaks it down per market + per process (+ FX), so the
// single headline number is never read as "everything is this fresh".
function FreshnessItem({ data }: { data: DataFreshness }) {
  const freshest =
    data.markets
      .map((m) => m.metrics)
      .filter((s): s is string => !!s)
      .sort()
      .at(-1) ?? null;
  const refresh = data.refresh ?? [];
  // `blocked` counts: a market with a scheduled source that cannot run is not
  // being refreshed, however recently a sibling source ingested.
  const staleMarkets = refresh.filter(
    (r) => r.status === 'stale' || r.status === 'unknown' || r.status === 'blocked',
  );

  const lines: string[] = [];
  // INGEST freshness first, because it is the one that answers "is this market
  // being kept up to date". The recompute stamps below are a different
  // question and used to be presented as if they were this one.
  if (refresh.length > 0) {
    lines.push('Source refresh — last successful ingest vs each market’s declared cadence:');
    const byStatus = { stale: 0, unknown: 0, due: 0, fresh: 0, blocked: 0 } as Record<
      string,
      number
    >;
    for (const r of refresh) byStatus[r.status] = (byStatus[r.status] ?? 0) + 1;
    lines.push(
      `  ${byStatus.fresh} fresh · ${byStatus.due} due · ${byStatus.stale} stale · ` +
        `${byStatus.blocked} blocked · ${byStatus.unknown} never recorded`,
    );
    for (const r of staleMarkets.slice(0, 10)) {
      const age = r.ageDays === null ? 'no ingest ever recorded' : `${Math.round(r.ageDays)}d ago`;
      lines.push(`  ${r.market} · ${age} · expects every ${r.cadenceDays}d (${r.mechanism})`);
    }
    if (staleMarkets.length > 10) lines.push(`  …and ${staleMarkets.length - 10} more`);
    lines.push('');
  }
  lines.push('Recompute stamps (NOT ingest — this runs nightly regardless):');
  for (const m of data.markets) {
    const parts = [`metrics ${shortStamp(m.metrics)}`];
    if (m.originationSignal) parts.push(`origination ${shortStamp(m.originationSignal)}`);
    if (m.sellerReadiness) parts.push(`seller-readiness ${shortStamp(m.sellerReadiness)}`);
    lines.push(`${m.country} · ${parts.join(' · ')}`);
  }
  if (data.fxRefreshedAt) lines.push(`FX · ${shortStamp(data.fxRefreshedAt)} (weekly)`);
  // Source staleness — only the ROTTING ones surface here (last row >35 days
  // old, i.e. beyond any monthly cadence); healthy sources stay quiet.
  const staleCutoff = Date.now() - 35 * 24 * 3600 * 1000;
  const stale = (data.sources ?? []).filter(
    (s) => new Date(s.lastIngestedAt).getTime() < staleCutoff,
  );
  if (stale.length > 0) {
    lines.push(`⚠ ${stale.length} stale source${stale.length === 1 ? '' : 's'} (>35d):`);
    for (const s of stale.slice(0, 8))
      lines.push(`  ${s.country} ${s.source} · last ${shortStamp(s.lastIngestedAt)}`);
    if (stale.length > 8) lines.push(`  …and ${stale.length - 8} more`);
  }
  // Calibration drift — the weekly backtest verdict, flagged markets only.
  const drifting = (data.calibrationDrift ?? []).filter((d) => d.driftFlag);
  if (drifting.length > 0) {
    lines.push(
      `⚠ model drift: ${drifting.map((d) => d.country).join(', ')} predicting outside claimed confidence`,
    );
  }
  // Dead scheduled jobs. Deliberately NOT folded into the tooltip like the
  // other warnings: on 2026-07-21 eight jobs had been failing nightly for
  // eight days and the only symptom was this headline reading "3d ago", which
  // looks like ordinary staleness. A stale timestamp is a SYMPTOM; a failing
  // job is the CAUSE, and the cause gets its own visible badge.
  const failing = data.failingJobs ?? [];
  // Stale markets get their OWN badge, for the reason the failing-jobs badge
  // exists: a market nobody is refreshing is a cause, and causes do not belong
  // folded into a tooltip behind a headline that reads "updated 3h ago".
  const staleLines =
    staleMarkets.length > 0
      ? [
          'Markets past their declared refresh cadence:',
          ...staleMarkets.map(
            (r) =>
              `  ${r.market} · ` +
              (r.ageDays === null
                ? 'no ingest run ever recorded'
                : `last ingest ${Math.round(r.ageDays)}d ago`) +
              ` · expects every ${r.cadenceDays}d · ${r.mechanism}` +
              (r.note ? ` — ${r.note}` : '') +
              (r.blockedSources ?? [])
                .map((b) => `\n    ✗ ${b.source} cannot run: ${b.reason}`)
                .join(''),
          ),
        ]
      : [];
  const jobLines =
    failing.length > 0
      ? [
          'Scheduled jobs whose last run FAILED:',
          ...failing.map(
            (j) =>
              `  ${j.name} · last run ${shortStamp(j.lastRunAt)} · ` +
              (j.lastOkAt ? `last OK ${shortStamp(j.lastOkAt)}` : 'NEVER succeeded in retention'),
          ),
        ]
      : [];
  // Bulk loss of protected data. Its own badge for the same cause-not-symptom
  // reason: in 2026-07 a crash emptied 18,067 scores and a bare clear destroyed
  // group attributions for ten days, and nothing on screen said so. The
  // triggers (migrations 0182/0183) already keep every removed row; what was
  // missing was anyone finding out. Only LARGE, unrestored operations badge —
  // see protected-changes.ts for the threshold — so ordinary ingest churn on
  // protected rows stays quiet.
  const bigLosses = largeUnrestoredOperations(data);
  const lossLines =
    bigLosses.length > 0
      ? [
          `Protected data removed or changed in bulk (last ${data.protectedChanges?.windowDays ?? 7}d, ` +
            `≥${LARGE_PROTECTED_OPERATION_ROWS.toLocaleString('en-GB')} rows in one operation, not restored):`,
          ...bigLosses.map(
            (o) =>
              `  txid ${o.txid} · ${o.tables.join(', ')} · ${describeOps(o.ops)} · ` +
              `${o.rows.toLocaleString('en-GB')} rows · ${shortStamp(o.lastAt)} · ` +
              (o.appName ? o.appName : 'unknown app'),
          ),
          '',
          'Every row was captured and is recoverable. Restore an operation with:',
          `  SELECT * FROM restore_protected(${bigLosses[0]!.txid});`,
          'Fill-only by default: it re-inserts missing rows and refills NULLs, never',
          'overwrites current values. Pass `, true` as a second argument to overwrite.',
        ]
      : [];

  return (
    <>
      <StatusItem>
        <span style={{ color: 'var(--text-tertiary)' }}>updated</span>
        <span
          data-numeric
          title={lines.join('\n')}
          style={{ textDecoration: 'underline dotted', cursor: 'default', whiteSpace: 'nowrap' }}
        >
          {relativeTime(freshest)}
        </span>
      </StatusItem>
      {failing.length > 0 && (
        <StatusItem>
          <span
            data-numeric
            title={jobLines.join('\n')}
            style={{
              color: 'var(--negative)',
              textDecoration: 'underline dotted',
              cursor: 'default',
              whiteSpace: 'nowrap',
            }}
          >
            ⚠ {failing.length} job{failing.length === 1 ? '' : 's'} failing
          </span>
        </StatusItem>
      )}
      {staleMarkets.length > 0 && (
        <StatusItem>
          <span
            data-numeric
            title={staleLines.join('\n')}
            style={{
              color: 'var(--color-warning-500, #b45309)',
              textDecoration: 'underline dotted',
              cursor: 'default',
              whiteSpace: 'nowrap',
            }}
          >
            ⚠ {staleMarkets.length} market{staleMarkets.length === 1 ? '' : 's'} unrefreshed
          </span>
        </StatusItem>
      )}
      {bigLosses.length > 0 && (
        <StatusItem>
          <span
            data-numeric
            title={lossLines.join('\n')}
            style={{
              color: 'var(--negative)',
              textDecoration: 'underline dotted',
              cursor: 'default',
              whiteSpace: 'nowrap',
            }}
          >
            ⚠ {bigLosses.length} bulk protected-data change{bigLosses.length === 1 ? '' : 's'}
          </span>
        </StatusItem>
      )}
    </>
  );
}
